/**
 * Centralized published site URL construction.
 *
 * All "View Live" buttons, subdomain displays, and route detection
 * should use these functions instead of hardcoding domain strings.
 *
 * Configured via VITE_PUBLISHED_DOMAIN env variable:
 *   - Local:      compete-pages-local.com
 *   - Staging:    competepages.com (or staging-specific)
 *   - Production: competepages.com
 */

type SiteLike = {
  customDomain?: string | null;
  subdomain?: string | null;
};

/**
 * Returns the base domain for published sites (e.g. "competepages.com").
 */
export function getPublishedDomain(): string {
  return import.meta.env.VITE_PUBLISHED_DOMAIN || 'competepages.com';
}

/**
 * Returns "http" for local dev domains, "https" otherwise.
 */
export function getPublishedProtocol(): string {
  const domain = getPublishedDomain();
  return domain.includes('local') ? 'http' : 'https';
}

/**
 * Full URL for a published site's root.
 * Custom domain → always https.
 * Subdomain → protocol based on environment.
 */
export function getPublishedSiteUrl(site: SiteLike): string {
  const custom = site.customDomain?.trim();
  if (custom) {
    return `https://${custom}`;
  }
  return `${getPublishedProtocol()}://${site.subdomain}.${getPublishedDomain()}`;
}

/**
 * Host-only label for dashboards (matches {@link getPublishedSiteUrl} host, no scheme).
 * Custom domain takes precedence when set.
 */
export function getPublishedSiteHostDisplay(site: SiteLike): string | null {
  const custom = site.customDomain?.trim();
  if (custom) return custom;
  const sub = site.subdomain?.trim();
  if (!sub) return null;
  return `${sub}.${getPublishedDomain()}`;
}

/**
 * Full URL for a specific page on a published site.
 * Home pages get the root URL, others get /slug appended.
 */
export function getPublishedPageUrl(site: SiteLike, slug: string, isHomePage?: boolean): string {
  const baseUrl = getPublishedSiteUrl(site);
  return isHomePage ? baseUrl : `${baseUrl}/${slug}`;
}
